// import { useEffect, useMemo, useState } from 'react';
// import { getIn } from 'formik';
// import type { FormikProps } from 'formik';
// import type { DiscountMode, DocCalcPaths } from './types';
// import { calcDocumentTotals } from './engine';
// import { clamp, num, round2 } from './round';

// export function useDocumentCalculator<TValues>(
//   formik: FormikProps<TValues>,
//   paths: DocCalcPaths,
//   opts?: {
//     getLineMode?: (line: any, index: number) => DiscountMode;
//   }
// ) {
//   const { values, setFieldValue, touched } = formik;

//   const lines = (getIn(values, paths.lines) || []) as any[];

//   const [headerMode, setHeaderMode] = useState<DiscountMode>(() => {
//     const r = num(getIn(values, paths.headerDiscountRate));
//     const a = num(getIn(values, paths.headerDiscountAmount));
//     return a > 0 && r === 0 ? 'AMOUNT' : 'RATE';
//   });

//   // ✅ auto-select mode on load (without forcing when user is editing)
//   useEffect(() => {
//     const r = num(getIn(values, paths.headerDiscountRate));
//     const a = num(getIn(values, paths.headerDiscountAmount));

//     const touchedRate = Boolean(getIn(touched as any, paths.headerDiscountRate));
//     const touchedAmt = Boolean(getIn(touched as any, paths.headerDiscountAmount));
//     if (touchedRate || touchedAmt) return;

//     if (r === 0 && a > 0) setHeaderMode('AMOUNT');
//     else if (a === 0 && r > 0) setHeaderMode('RATE');
//     // else keep current mode
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [getIn(values, paths.headerDiscountRate), getIn(values, paths.headerDiscountAmount)]);

//   const getLineMode =
//     opts?.getLineMode ?? ((line: any) => (String(line?._discountMode || 'RATE') === 'AMOUNT' ? 'AMOUNT' : 'RATE'));

//   const totals = useMemo(() => {
//     return calcDocumentTotals({
//       lines,
//       headerDiscountRate: getIn(values, paths.headerDiscountRate),
//       headerDiscountAmount: getIn(values, paths.headerDiscountAmount),
//       headerMode,
//       getLineMode
//     });
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [lines, getIn(values, paths.headerDiscountRate), getIn(values, paths.headerDiscountAmount), headerMode]);

//   // ✅ write only derived header fields (no syncing between discountRate/discountAmount)
//   useEffect(() => {
//     const setIfChanged = (path: string, next: number) => {
//       const cur = num(getIn(values, path));
//       if (Math.abs(cur - next) > 0.0001) setFieldValue(path, next, false);
//     };

//     setIfChanged(paths.grossAmount, totals.linesSubtotal);
//     setIfChanged(paths.vat, totals.vatTotal);
//     setIfChanged(paths.netAmount, totals.netTotal);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [totals]);

//   const onHeaderDiscountRateChange = (rate: number) => {
//     setHeaderMode('RATE');
//     setFieldValue(paths.headerDiscountRate, clamp(round2(rate), 0, 100), false);

//     // ✅ صفّر مبلغ الخصم
//     setFieldValue(paths.headerDiscountAmount, 0, false);
//   };

//   const onHeaderDiscountAmountChange = (amt: number) => {
//     setHeaderMode('AMOUNT');

//     const gross = (totals as any).grossTotal ?? round2(totals.linesSubtotal + totals.vatTotal);
//     setFieldValue(paths.headerDiscountAmount, clamp(round2(amt), 0, gross), false);

//     // ✅ صفّر نسبة الخصم
//     setFieldValue(paths.headerDiscountRate, 0, false);
//   };

//   return {
//     headerMode,
//     setHeaderMode,
//     totals,
//     onHeaderDiscountRateChange,
//     onHeaderDiscountAmountChange
//   };
// }






import { useEffect, useMemo, useState } from 'react';
import { getIn } from 'formik';
import type { FormikProps } from 'formik';
import type { DiscountMode, DocCalcPaths } from './types';
import { calcDocumentTotals } from './engine';
import { clamp, num, round2 } from './round';

export function useDocumentCalculator<TValues>(
  formik: FormikProps<TValues>,
  paths: DocCalcPaths,
  opts?: {
    getLineMode?: (line: any, index: number) => DiscountMode;
  }
) {
  const { values, setFieldValue, touched } = formik;

  const lines = (getIn(values, paths.lines) || []) as any[];

  const [headerMode, setHeaderMode] = useState<DiscountMode>(() => {
    const r = num(getIn(values, paths.headerDiscountRate));
    const a = num(getIn(values, paths.headerDiscountAmount));
    return a > 0 && r === 0 ? 'AMOUNT' : 'RATE';
  });

  // ✅ auto-select mode on load (without forcing when user is editing)
  useEffect(() => {
    const r = num(getIn(values, paths.headerDiscountRate));
    const a = num(getIn(values, paths.headerDiscountAmount));

    const touchedRate = Boolean(getIn(touched as any, paths.headerDiscountRate));
    const touchedAmt = Boolean(getIn(touched as any, paths.headerDiscountAmount));
    if (touchedRate || touchedAmt) return;

    if (r === 0 && a > 0) setHeaderMode('AMOUNT');
    else if (a === 0 && r > 0) setHeaderMode('RATE');
    // else keep current mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIn(values, paths.headerDiscountRate), getIn(values, paths.headerDiscountAmount)]);

  const getLineMode =
    opts?.getLineMode ?? ((line: any) => (String(line?._discountMode || 'RATE') === 'AMOUNT' ? 'AMOUNT' : 'RATE'));

  const totals = useMemo(() => {
    return calcDocumentTotals({
      lines,
      headerDiscountRate: getIn(values, paths.headerDiscountRate),
      headerDiscountAmount: getIn(values, paths.headerDiscountAmount),
      headerMode,
      getLineMode
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, getIn(values, paths.headerDiscountRate), getIn(values, paths.headerDiscountAmount), headerMode]);

  // ✅ write only derived header fields (no syncing between discountRate/discountAmount)
  useEffect(() => {
    const setIfChanged = (path: string, next: number) => {
      const cur = num(getIn(values, path));
      if (Math.abs(cur - next) > 0.0001) setFieldValue(path, next, false);
    };

    setIfChanged(paths.grossAmount, totals.linesSubtotal);
    setIfChanged(paths.vat, totals.vatTotal);
    setIfChanged(paths.netAmount, totals.netTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals]);

  const onHeaderDiscountRateChange = (rate: number) => {
    setHeaderMode('RATE');
    setFieldValue(paths.headerDiscountRate, clamp(round2(rate), 0, 100), false);

    // ✅ صفّر مبلغ الخصم
    setFieldValue(paths.headerDiscountAmount, 0, false);
  };

  const onHeaderDiscountAmountChange = (amt: number) => {
    setHeaderMode('AMOUNT');

    const gross = (totals as any).grossTotal ?? round2(totals.linesSubtotal + totals.vatTotal);
    setFieldValue(paths.headerDiscountAmount, clamp(round2(amt), 0, gross), false);

    // ✅ صفّر نسبة الخصم
    setFieldValue(paths.headerDiscountRate, 0, false);
  };

  return {
    headerMode,
    setHeaderMode,
    totals,
    onHeaderDiscountRateChange,
    onHeaderDiscountAmountChange
  };
}
